import {useEffect, useMemo, useState} from 'react';

import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputBase,
    Stack,
    Typography,
} from '@mui/material';

import {C} from '../constants';

// Section keys map to the four content surfaces the user can include or
// exclude before publishing. Each listing's items[] uses the same keys so
// the dialog can correlate a checkbox to that row across every listing.
const SHOPIFY_SECTION_KEYS = ['title', 'description', 'bullets', 'faqs'];
const GMC_SECTION_KEYS = ['title', 'description'];
const EMPTY_LIST = [];

const SECTION_LABELS = {
    title: 'Title',
    description: 'Description',
    bullets: 'Bullets',
    faqs: 'FAQs',
};

export const buildPublishTargetRows = (listings = []) => {
    const rows = [];
    const groupsByKey = new Map();

    listings.forEach((listing) => {
        if (listing.superSku) {
            if (!groupsByKey.has(listing.superSku)) {
                const groupRow = {
                    id: `group:${listing.superSku}`,
                    type: 'group',
                    label: listing.superSku,
                    listingIds: [],
                    listings: [],
                };
                groupsByKey.set(listing.superSku, groupRow);
                rows.push(groupRow);
            }
            const groupRow = groupsByKey.get(listing.superSku);
            groupRow.listingIds.push(listing.id);
            groupRow.listings.push(listing);
            return;
        }

        rows.push({
            id: `listing:${listing.id}`,
            type: 'listing',
            label: listing.name || listing.sku || 'Untitled listing',
            listingIds: [listing.id],
            listings: [listing],
        });
    });

    return rows;
};

// Computes what will actually land when the user confirms. Mirrors the
// filtering rules used by publishSingleListing / pushSingleListingToGmc in
// contentReview/index.jsx — single source of truth for "what counts as
// approved" should eventually be extracted, but for now this replicates
// the logic so the preview matches reality exactly.
const summarizeListingApproval = (listing, destination) => {
    const titleField = listing.fields.find((f) => f.type === 'title');
    const descField = listing.fields.find((f) => f.type === 'description');
    const nonEmptyBullets = listing.fields.filter(
        (f) => f.type === 'bullet' && f.text && f.text.trim(),
    );
    // FAQ fields are publishable when both question + answer are filled.
    const nonEmptyFaqs = listing.fields.filter(
        (f) =>
            f.type === 'faq' &&
            f.question &&
            f.question.trim() &&
            f.text &&
            f.text.trim(),
    );

    const titleApproved = titleField?.status === 'approved';
    const descApproved = descField?.status === 'approved';
    const approvedBullets = nonEmptyBullets.filter((b) => b.status === 'approved');
    const allBulletsApproved =
        nonEmptyBullets.length > 0 && approvedBullets.length === nonEmptyBullets.length;
    // FAQs follow the same all-or-nothing rule as bullets — the FAQPage
    // JSON-LD schema is published as a single set, so pushing a partial
    // set would broadcast an incomplete FAQ list with the schema marker.
    const approvedFaqs = nonEmptyFaqs.filter((f) => f.status === 'approved');
    const allFaqsApproved =
        nonEmptyFaqs.length > 0 && approvedFaqs.length === nonEmptyFaqs.length;

    if (destination === 'gmc') {
        return {
            id: listing.id,
            name: listing.name || listing.sku || 'Untitled listing',
            items: [
                titleField && {key: 'title', label: 'Title', approved: titleApproved},
                descField && {
                    key: 'description',
                    label: 'Description',
                    approved: descApproved,
                },
            ].filter(Boolean),
        };
    }

    // Shopify
    const bulletsCount = nonEmptyBullets.length;
    const faqsCount = nonEmptyFaqs.length;
    return {
        id: listing.id,
        name: listing.name || listing.sku || 'Untitled listing',
        items: [
            titleField && {key: 'title', label: 'Title', approved: titleApproved},
            descField && {
                key: 'description',
                label: 'Description',
                approved: descApproved,
            },
            bulletsCount > 0 && {
                key: 'bullets',
                label: `Bullets (${approvedBullets.length}/${bulletsCount} approved)`,
                approved: allBulletsApproved,
                note: !allBulletsApproved
                    ? 'All-or-nothing: bullets only publish when every one is approved'
                    : null,
            },
            faqsCount > 0 && {
                key: 'faqs',
                label: `FAQs (${approvedFaqs.length}/${faqsCount} approved)`,
                approved: allFaqsApproved,
                note: !allFaqsApproved
                    ? 'All-or-nothing: FAQs only publish when every Q&A is approved'
                    : null,
            },
        ].filter(Boolean),
    };
};

export default function PublishConfirmDialog({
    open,
    destination,
    listings = EMPTY_LIST,
    initialSelectedListingIds = EMPTY_LIST,
    onConfirm,
    onCancel,
}) {
    const summary = useMemo(
        () => listings.map((listing) => summarizeListingApproval(listing, destination)),
        [listings, destination],
    );
    const targetRows = useMemo(() => buildPublishTargetRows(listings), [listings]);
    const [selectedListingIds, setSelectedListingIds] = useState(
        () => new Set(initialSelectedListingIds),
    );
    const [targetSearch, setTargetSearch] = useState('');

    useEffect(() => {
        if (!open) return;
        const validIds = new Set(listings.map((listing) => listing.id));
        const initialIds = initialSelectedListingIds.filter((id) => validIds.has(id));
        setSelectedListingIds(
            new Set(initialIds.length ? initialIds : listings[0]?.id ? [listings[0].id] : []),
        );
        setTargetSearch('');
    }, [open, initialSelectedListingIds, listings]);

    // A section is available when at least one listing in the batch has
    // an approved item for that key. Unavailable sections can't be
    // toggled (the checkbox is disabled) — they have nothing to send.
    const availableSectionKeys = useMemo(() => {
        const available = new Set();
        summary.forEach((listingSummary) => {
            listingSummary.items.forEach((item) => {
                if (item.approved) available.add(item.key);
            });
        });
        return available;
    }, [summary]);

    const sectionKeys =
        destination === 'gmc' ? GMC_SECTION_KEYS : SHOPIFY_SECTION_KEYS;

    // enabledSections defaults all available sections to on every time
    // the dialog opens; closes the dialog with the user's choices reset
    // so the next open starts from a clean default.
    const [enabledSections, setEnabledSections] = useState(() => {
        const initial = {};
        sectionKeys.forEach((key) => {
            initial[key] = availableSectionKeys.has(key);
        });
        return initial;
    });
    useEffect(() => {
        if (!open) return;
        const next = {};
        sectionKeys.forEach((key) => {
            next[key] = availableSectionKeys.has(key);
        });
        setEnabledSections(next);
    }, [open, availableSectionKeys, sectionKeys]);

    const toggleSection = (key) => {
        if (!availableSectionKeys.has(key)) return;
        setEnabledSections((current) => ({...current, [key]: !current[key]}));
    };

    const toggleTargetRow = (row) => {
        setSelectedListingIds((current) => {
            const next = new Set(current);
            const allSelected = row.listingIds.every((id) => next.has(id));
            row.listingIds.forEach((id) => {
                if (allSelected) next.delete(id);
                else next.add(id);
            });
            return next;
        });
    };

    const normalizedSearch = targetSearch.trim().toLowerCase();
    const filteredTargetRows = targetRows.filter((row) => {
        if (!normalizedSearch) return true;
        return [
            row.label,
            ...row.listings.flatMap((listing) => [listing.name, listing.sku]),
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });

    // Recompute willPublish per listing based on current section toggles.
    // A listing publishes when at least one of its approved items is in
    // an enabled section.
    const listingWillPublish = (listingSummary) =>
        listingSummary.items.some(
            (item) => item.approved && enabledSections[item.key],
        );

    const selectedSummary = summary.filter((listingSummary) =>
        selectedListingIds.has(listingSummary.id),
    );
    const selectedCount = selectedSummary.length;
    const isBatch = listings.length > 1;
    const publishCount = selectedSummary.filter(listingWillPublish).length;
    const skipCount = selectedCount - publishCount;

    const destinationLabel =
        destination === 'gmc' ? 'Google Merchant' : 'Shopify';
    const destinationColor = destination === 'gmc' ? C.purple : C.blue;
    const title = isBatch
        ? `${destination === 'gmc' ? 'Push' : 'Publish'} selected listings to ${destinationLabel}`
        : `${destination === 'gmc' ? 'Push' : 'Publish'} to ${destinationLabel}`;

    const confirmLabel = publishCount === 0
        ? destination === 'gmc' ? 'Nothing to push' : 'Nothing to publish'
        : destination === 'gmc'
            ? `Push ${publishCount}`
            : `Publish ${publishCount}`;

    const handleConfirm = () => {
        if (publishCount === 0) return;
        onConfirm?.({
            enabledSections: {...enabledSections},
            targetListingIds: listings
                .filter((listing) => selectedListingIds.has(listing.id))
                .map((listing) => listing.id),
        });
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 800, color: C.ink}}>
                {title}
            </DialogTitle>

            <DialogContent dividers sx={{background: C.surfaceAlt}}>
                <Stack gap="12px">
                    {isBatch && (
                        <Box
                            sx={{
                                border: `1px solid ${C.border}`,
                                borderRadius: '10px',
                                padding: '10px',
                                background: C.surface,
                            }}>
                            <Stack direction="row" alignItems="center" gap="8px">
                                <InputBase
                                    value={targetSearch}
                                    onChange={(event) => setTargetSearch(event.target.value)}
                                    placeholder="Filter products or groups"
                                    inputProps={{'aria-label': 'Filter publish targets'}}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        border: `1px solid ${C.border}`,
                                        borderRadius: '8px',
                                        padding: '3px 9px',
                                        fontSize: 12,
                                        background: C.surfaceAlt,
                                    }}
                                />
                                <ButtonBase
                                    onClick={() =>
                                        setSelectedListingIds(
                                            new Set(listings.map((listing) => listing.id)),
                                        )
                                    }
                                    sx={{fontSize: 11, fontWeight: 700, color: destinationColor}}>
                                    Select all
                                </ButtonBase>
                                <ButtonBase
                                    onClick={() => setSelectedListingIds(new Set())}
                                    sx={{fontSize: 11, fontWeight: 700, color: C.textSoft}}>
                                    Clear
                                </ButtonBase>
                            </Stack>

                            <Stack
                                gap="4px"
                                sx={{maxHeight: 210, overflowY: 'auto', marginTop: '8px'}}>
                                {filteredTargetRows.map((row) => {
                                    const selectedCountForRow = row.listingIds.filter((id) =>
                                        selectedListingIds.has(id),
                                    ).length;
                                    const checked =
                                        selectedCountForRow === row.listingIds.length;
                                    const indeterminate =
                                        selectedCountForRow > 0 && !checked;
                                    const firstListing = row.listings[0] || {};
                                    const rowLabel = row.type === 'group'
                                        ? `Select group ${row.label}`
                                        : `Select SKU ${firstListing.sku || row.label}`;

                                    return (
                                        <Stack
                                            key={row.id}
                                            direction="row"
                                            alignItems="center"
                                            gap="8px"
                                            onClick={() => toggleTargetRow(row)}
                                            sx={{
                                                borderRadius: '8px',
                                                padding: '5px 7px',
                                                cursor: 'pointer',
                                                background: checked
                                                    ? `${destinationColor}0D`
                                                    : 'transparent',
                                                '&:hover': {background: `${destinationColor}12`},
                                            }}>
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                indeterminate={indeterminate}
                                                onChange={() => toggleTargetRow(row)}
                                                onClick={(event) => event.stopPropagation()}
                                                inputProps={{'aria-label': rowLabel}}
                                                sx={{
                                                    padding: '2px',
                                                    color: C.textFaint,
                                                    '&.Mui-checked': {color: destinationColor},
                                                }}
                                            />
                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: C.ink,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {row.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 10.5,
                                                        color: C.textSoft,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {row.type === 'group'
                                                        ? `${row.listingIds.length} SKUs`
                                                        : firstListing.sku || 'SKU'}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="span"
                                                sx={{
                                                    borderRadius: 999,
                                                    padding: '2px 7px',
                                                    background:
                                                        row.type === 'group'
                                                            ? C.amberL
                                                            : C.surfaceAlt,
                                                    color:
                                                        row.type === 'group'
                                                            ? C.amber
                                                            : C.textSoft,
                                                    fontSize: 9.5,
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                }}>
                                                {row.type === 'group' ? 'Group' : 'SKU'}
                                            </Box>
                                        </Stack>
                                    );
                                })}
                                {!filteredTargetRows.length && (
                                    <Typography
                                        sx={{fontSize: 11.5, color: C.textSoft, padding: '10px'}}>
                                        No products or groups match this filter.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Top-line summary */}
                    {isBatch ? (
                        <Box>
                            <Typography sx={{fontSize: 13, color: C.ink, lineHeight: 1.5}}>
                                <strong>{publishCount}</strong> of {selectedCount} selected listings
                                will be {destination === 'gmc' ? 'pushed' : 'published'}
                                {skipCount > 0
                                    ? `, ${skipCount} skipped (no approved content).`
                                    : '.'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography sx={{fontSize: 13, color: C.ink, lineHeight: 1.5}}>
                                {listingWillPublish(summary[0] || {items: []})
                                    ? `Ready to ${destination === 'gmc' ? 'push' : 'publish'}:`
                                    : 'Nothing will be published — no approved content selected:'}
                            </Typography>
                            <Typography sx={{fontSize: 13, fontWeight: 700, color: C.ink, marginTop: '4px'}}>
                                {summary[0]?.name}
                            </Typography>
                        </Box>
                    )}

                    {/* Section toggles — applied across every listing in the batch. */}
                    <Box
                        sx={{
                            border: `1px solid ${C.border}`,
                            borderRadius: '10px',
                            padding: '8px 12px',
                            background: C.surface,
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                textTransform: 'uppercase',
                                color: C.textSoft,
                                marginBottom: '4px',
                            }}>
                            Include in update
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap="2px 14px">
                            {sectionKeys.map((key) => {
                                const isAvailable = availableSectionKeys.has(key);
                                const isChecked = Boolean(enabledSections[key]) && isAvailable;
                                return (
                                    <Stack
                                        key={key}
                                        direction="row"
                                        alignItems="center"
                                        gap="2px"
                                        onClick={() => toggleSection(key)}
                                        sx={{
                                            cursor: isAvailable ? 'pointer' : 'default',
                                            opacity: isAvailable ? 1 : 0.45,
                                            userSelect: 'none',
                                        }}>
                                        <Checkbox
                                            size="small"
                                            checked={isChecked}
                                            disabled={!isAvailable}
                                            onChange={() => toggleSection(key)}
                                            onClick={(event) => event.stopPropagation()}
                                            sx={{
                                                padding: '2px',
                                                color: C.textFaint,
                                                '&.Mui-checked': {color: destinationColor},
                                            }}
                                        />
                                        <Typography sx={{fontSize: 12.5, color: C.ink}}>
                                            {SECTION_LABELS[key]}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>

                    {/* Per-listing detail */}
                    <Stack
                        gap="8px"
                        sx={{
                            maxHeight: 340,
                            overflowY: 'auto',
                            paddingRight: '4px',
                        }}>
                        {selectedSummary.map((listingSummary) => {
                            const willPublish = listingWillPublish(listingSummary);
                            return (
                                <Box
                                    key={listingSummary.id}
                                    sx={{
                                        border: `1px solid ${willPublish ? `${C.teal}33` : C.border}`,
                                        borderRadius: '10px',
                                        padding: '9px 11px',
                                        background: willPublish ? C.surface : `${C.textFaint}08`,
                                        opacity: willPublish ? 1 : 0.75,
                                    }}>
                                    {isBatch && (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: willPublish ? C.ink : C.textSoft,
                                                marginBottom: '4px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={listingSummary.name}>
                                            {listingSummary.name}
                                        </Typography>
                                    )}
                                    <Stack gap="3px">
                                        {listingSummary.items.map((item) => {
                                            const willSend = item.approved && enabledSections[item.key];
                                            return (
                                                <Stack
                                                    key={item.label}
                                                    direction="row"
                                                    alignItems="center"
                                                    gap="8px">
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 14,
                                                            height: 14,
                                                            borderRadius: '50%',
                                                            background: willSend
                                                                ? `${C.teal}22`
                                                                : `${C.textFaint}15`,
                                                            color: willSend ? C.teal : C.textFaint,
                                                            fontSize: 9,
                                                            fontWeight: 800,
                                                            flexShrink: 0,
                                                        }}>
                                                        {willSend ? '✓' : '–'}
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 11.5,
                                                            color: willSend ? C.ink : C.textFaint,
                                                            flex: 1,
                                                            minWidth: 0,
                                                            textDecoration:
                                                                item.approved && !enabledSections[item.key]
                                                                    ? 'line-through'
                                                                    : 'none',
                                                        }}>
                                                        {item.label}
                                                    </Typography>
                                                </Stack>
                                            );
                                        })}
                                        {listingSummary.items.some((i) => i.note) && (
                                            <Typography
                                                sx={{
                                                    fontSize: 10.5,
                                                    color: C.textFaint,
                                                    marginTop: '3px',
                                                    lineHeight: 1.45,
                                                    paddingLeft: '22px',
                                                }}>
                                                {listingSummary.items.find((i) => i.note)?.note}
                                            </Typography>
                                        )}
                                        {!willPublish && !isBatch && (
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: C.amber,
                                                    marginTop: '4px',
                                                    lineHeight: 1.45,
                                                }}>
                                                Enable at least one approved section to publish.
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>

                    {publishCount > 0 && (
                        <Alert
                            severity="warning"
                            sx={{
                                fontSize: 12,
                                alignItems: 'flex-start',
                                '& .MuiAlert-message': {lineHeight: 1.5},
                            }}>
                            This overwrites existing{' '}
                            {destination === 'gmc'
                                ? 'title and description overrides in your supplemental feed'
                                : 'product content in Shopify'}
                            . Unapproved fields are never sent.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{padding: '12px 20px', background: C.surface}}>
                <Button onClick={onCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={publishCount === 0}
                    sx={{
                        background: destinationColor,
                        '&:hover': {background: destinationColor, opacity: 0.9},
                    }}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
